export const CHECK_STATE = {
  UNCHECKED: 'unchecked',
  CHECKED: 'checked',
  INDETERMINATE: 'indeterminate',
} as const;

export type CheckState = (typeof CHECK_STATE)[keyof typeof CHECK_STATE];

import { useCallback, useMemo, useState } from 'react';

import { ServiceItem } from '@/modules/ProcessingDefinition/Services/Services.types';

import { SelectedService } from '../AddServicesModal.types';

interface CategoryState {
  selectedServiceIds: Set<string>;
  allServices: ServiceItem[];
}

const useAddServicesSelection = () => {
  const [selectionMap, setSelectionMap] = useState<Map<string, CategoryState>>(new Map());

  const toggleService = useCallback((categoryType: string, service: ServiceItem) => {
    setSelectionMap((prev) => {
      const next = new Map(prev);
      const state = next.get(categoryType) || {
        selectedServiceIds: new Set<string>(),
        allServices: [],
      };
      const newIds = new Set(state.selectedServiceIds);
      let updatedAllServices = state.allServices;

      if (newIds.has(service.id)) {
        newIds.delete(service.id);
      } else {
        newIds.add(service.id);
        if (!state.allServices.some((s) => s.id === service.id)) {
          updatedAllServices = [...state.allServices, service];
        }
      }

      next.set(categoryType, { selectedServiceIds: newIds, allServices: updatedAllServices });
      return next;
    });
  }, []);

  const selectAllCategory = useCallback((categoryType: string, services: ServiceItem[]) => {
    setSelectionMap((prev) => {
      const next = new Map(prev);
      const allIds = new Set(services.map((service) => service.id));
      next.set(categoryType, {
        selectedServiceIds: allIds,
        allServices: services,
      });
      return next;
    });
  }, []);

  const deselectAllCategory = useCallback((categoryType: string) => {
    setSelectionMap((prev) => {
      const next = new Map(prev);
      const state = next.get(categoryType);
      if (state) {
        next.set(categoryType, { ...state, selectedServiceIds: new Set() });
      }
      return next;
    });
  }, []);

  const isServiceSelected = useCallback(
    (categoryType: string, serviceId: string) => {
      const state = selectionMap.get(categoryType);
      return state?.selectedServiceIds.has(serviceId) ?? false;
    },
    [selectionMap]
  );

  const getCategoryCheckState = useCallback(
    (categoryType: string, totalServices: number): CheckState => {
      const state = selectionMap.get(categoryType);
      if (!state || state.selectedServiceIds.size === 0) return CHECK_STATE.UNCHECKED;
      if (state.selectedServiceIds.size >= totalServices) return CHECK_STATE.CHECKED;
      return CHECK_STATE.INDETERMINATE;
    },
    [selectionMap]
  );

  const getSelectedServices = useCallback((): SelectedService[] => {
    const result: SelectedService[] = [];

    selectionMap.forEach((state, categoryType) => {
      state.selectedServiceIds.forEach((serviceId) => {
        const service = state.allServices.find((service) => service.id === serviceId);
        if (service) {
          result.push({
            id: service.id,
            name: service.name,
            serviceType: service.serviceType,
            categoryType,
          });
        }
      });
    });

    return result;
  }, [selectionMap]);

  const totalSelected = useMemo(() => {
    let count = 0;
    selectionMap.forEach((state) => {
      count += state.selectedServiceIds.size;
    });
    return count;
  }, [selectionMap]);

  const reset = useCallback(() => {
    setSelectionMap(new Map());
  }, []);

  return {
    toggleService,
    selectAllCategory,
    deselectAllCategory,
    isServiceSelected,
    getCategoryCheckState,
    getSelectedServices,
    totalSelected,
    reset,
  };
};

export default useAddServicesSelection;
